import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Loader } from "@/shared/ui/Loader";
import { DatabaseBackup, Download } from "lucide-react";
import {
  useBackupStatusQuery,
  useDownloadDatabaseBackupMutation,
} from "../api/queries";

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("lo-LA");
  } catch {
    return iso;
  }
}

export function BackupPage() {
  const { data: status, isLoading } = useBackupStatusQuery(true);
  const download = useDownloadDatabaseBackupMutation();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ສຳຮອງຂໍ້ມູນ",
              to: "/app/system/backup",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mx-auto flex max-w-2xl flex-col gap-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              ສຳຮອງຂໍ້ມູນ (ດາວໂຫຼດດ້ວຍຕົນເອງ)
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              ບໍ່ມີບ່ອນເກັບ backup ອັດຕະໂນມັດ — ໃຫ້ຜູ້ດູແລດາວໂຫຼດໄຟລ໌ SQL
              (.gz) ແລະ ເກັບໄວ້ຄອມພິວເຕີ / USB / cloud ສ່ວນຕົວ.
            </p>
          </div>

          {isLoading ? (
            <Loader />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DatabaseBackup className="size-5" />
                  PostgreSQL (Mineral)
                </CardTitle>
                <CardDescription>
                  ຄວາມຖີ່ແຈ້ງເຕືອນ: ທຸກ {status?.intervalDays ?? 7} ວັນ · restore
                  ດ້ວຍ{" "}
                  <code className="text-xs">gunzip -c file.sql.gz | psql …</code>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <dl className="grid gap-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">ສະຖານະ</dt>
                    <dd className="font-medium">
                      {status?.due ? (
                        <span className="text-amber-600 dark:text-amber-400">
                          ຄວນສຳຮອງແລ້ວ
                        </span>
                      ) : (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          ຍັງບໍ່ເຖິງກຳນົດ
                        </span>
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">ດາວໂຫຼດຄັ້ງລ່າສຸດ</dt>
                    <dd>{formatDateTime(status?.lastDownloadAt ?? null)}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">ໂດຍ</dt>
                    <dd>{status?.lastDownloadByName ?? "—"}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">ຄັ້ງຖັດໄປ (ປະມານ)</dt>
                    <dd>{formatDateTime(status?.nextDueAt ?? null)}</dd>
                  </div>
                </dl>

                {status?.messageLo ? (
                  <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                    {status.messageLo}
                  </p>
                ) : null}

                <Button
                  className="w-full sm:w-auto"
                  disabled={download.isPending}
                  onClick={() => download.mutate()}
                >
                  <Download className="mr-2 size-4" />
                  {download.isPending
                    ? "ກຳລັງສ້າງໄຟລ໌…"
                    : "ດາວໂຫຼດ backup (.sql.gz)"}
                </Button>

                <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-xs">
                  <li>ໄຟລ໌ມີຂໍ້ມູນລູກຄ້າ, ຄຳສັ່ງ, wallet — ຖືວ່າເປັນຄວາມລັບ.</li>
                  <li>MinIO (ຮູບ upload) ບໍ່ລວມໃນໄຟລ໌ນີ້ — ສຳຮອງແຍກຖ້າຈຳເປັນ.</li>
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      </Main>
    </>
  );
}
